import { Link } from 'react-router-dom';
import { Shield, Lock, Eye, FileText, Users, Globe } from 'lucide-react';

function PrivacyPolicy() {
  return (
    <div className="pt-28">
      {/* Hero Section */}
      <section className="bg-black py-24 relative overflow-hidden">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="absolute inset-0 shimmer-wave"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="flex items-center justify-center mb-6">
            <Shield className="w-20 h-20 text-gold" />
          </div>
          <span className="text-gold text-sm font-semibold tracking-[0.3em] uppercase mb-4 block">Legal Information</span>
          <div className="w-24 h-1 bg-gold mx-auto mb-8"></div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Privacy <span className="text-gold">Policy</span>
          </h1>
          <p className="text-gray-300 text-xl max-w-4xl mx-auto">
            Williams Diversified LLC is committed to protecting your privacy and maintaining the security of your personal information.
          </p>
          <p className="text-gray-400 text-sm mt-4">
            Last Updated: January 2025
          </p>
        </div>
      </section>

      {/* Privacy Overview Cards */}
      <section className="bg-black py-16 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="bg-black border border-gold/30 rounded-lg p-8 hover:border-gold transition-all">
              <Lock className="w-12 h-12 text-gold mb-4" />
              <h3 className="text-white font-bold text-xl mb-3">Data Security</h3>
              <p className="text-gray-400">Your information is protected with industry-standard security measures</p>
            </div>
            <div className="bg-black border border-gold/30 rounded-lg p-8 hover:border-gold transition-all">
              <Eye className="w-12 h-12 text-gold mb-4" />
              <h3 className="text-white font-bold text-xl mb-3">Transparency</h3>
              <p className="text-gray-400">We clearly explain how we collect, use, and protect your data</p>
            </div>
            <div className="bg-black border border-gold/30 rounded-lg p-8 hover:border-gold transition-all">
              <Users className="w-12 h-12 text-gold mb-4" />
              <h3 className="text-white font-bold text-xl mb-3">Your Rights</h3>
              <p className="text-gray-400">You have control over your personal information and privacy choices</p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="bg-black py-16 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-gray-300 space-y-12">
            
            {/* Section 1: Information We Collect */}
            <div className="pt-28">
              <div className="flex items-center space-x-3 mb-6">
                <FileText className="w-8 h-8 text-gold" />
                <h2 className="text-3xl font-bold text-white font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                  1. Information We Collect
                </h2>
              </div>
              <div className="space-y-4 text-lg">
                <p>We collect information that you provide directly to us, including:</p>
                <ul className="space-y-3 ml-6">
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span><strong className="text-white">Contact Information:</strong> Name, email address, phone number, mailing address, and company information when you submit inquiries, request quotes, or apply for positions.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span><strong className="text-white">Professional Information:</strong> Resume, work history, certifications, and references when applying for employment or vendor opportunities.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span><strong className="text-white">Project Information:</strong> Details about your construction or service needs, project specifications, timelines, and budgets.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span><strong className="text-white">Communication Records:</strong> Records of your correspondence with us, including emails, phone calls, and form submissions.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span><strong className="text-white">Technical Information:</strong> IP address, browser type, device information, and website usage data collected through cookies and similar technologies.</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Section 2: How We Use Your Information */}
            <div className="pt-28">
              <div className="flex items-center space-x-3 mb-6">
                <Globe className="w-8 h-8 text-gold" />
                <h2 className="text-3xl font-bold text-white font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                  2. How We Use Your Information
                </h2>
              </div>
              <div className="space-y-4 text-lg">
                <p>Williams Diversified LLC uses your information for the following purposes:</p>
                <ul className="space-y-3 ml-6">
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span><strong className="text-white">Service Delivery:</strong> To respond to inquiries, provide quotes, manage projects, and deliver contracted services.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span><strong className="text-white">Employment Processing:</strong> To evaluate job applications, conduct background checks (with consent), and manage the hiring process.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span><strong className="text-white">Communication:</strong> To contact you about your projects, applications, or inquiries, and to provide customer support.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span><strong className="text-white">Business Operations:</strong> To improve our services, analyze website performance, and conduct internal business analytics.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span><strong className="text-white">Legal Compliance:</strong> To comply with applicable laws, regulations, and government contract requirements.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span><strong className="text-white">Marketing:</strong> To send you information about our services, capabilities, and industry updates (you may opt out at any time).</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Section 3: Information Sharing */}
            <div className="pt-28">
              <h2 className="text-3xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                3. Information Sharing and Disclosure
              </h2>
              <div className="space-y-4 text-lg">
                <p>We do not sell your personal information. We may share your information in the following circumstances:</p>
                <ul className="space-y-3 ml-6">
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span><strong className="text-white">Service Providers:</strong> With trusted third-party vendors who assist with business operations (e.g., background check services, IT support) under strict confidentiality agreements.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span><strong className="text-white">Government Entities:</strong> With government agencies when required for contract compliance or legal obligations.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span><strong className="text-white">Legal Requirements:</strong> When required by law, court order, or government regulation, or to protect our rights, property, or safety.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span><strong className="text-white">Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets, where your information may be transferred to the acquiring entity.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span><strong className="text-white">With Your Consent:</strong> With your explicit permission for specific purposes not covered above.</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Section 4: Data Security */}
            <div className="pt-28">
              <h2 className="text-3xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                4. Data Security
              </h2>
              <div className="space-y-4 text-lg">
                <p>
                  We implement appropriate technical and organizational security measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. These measures include:
                </p>
                <ul className="space-y-3 ml-6">
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Secure data encryption during transmission and storage</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Regular security assessments and vulnerability testing</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Restricted access to personal information on a need-to-know basis</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Employee training on data protection and confidentiality</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Compliance with government security standards for federal projects</span>
                  </li>
                </ul>
                <p className="mt-4">
                  While we take reasonable precautions to protect your information, no method of transmission over the internet or electronic storage is 100% secure. We cannot guarantee absolute security but continuously work to maintain the highest standards of data protection.
                </p>
              </div>
            </div>

            {/* Section 5: Your Rights and Choices */}
            <div className="pt-28">
              <h2 className="text-3xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                5. Your Rights and Choices
              </h2>
              <div className="space-y-4 text-lg">
                <p>You have the following rights regarding your personal information:</p>
                <ul className="space-y-3 ml-6">
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span><strong className="text-white">Access:</strong> Request access to the personal information we hold about you.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span><strong className="text-white">Correction:</strong> Request correction of inaccurate or incomplete information.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span><strong className="text-white">Deletion:</strong> Request deletion of your personal information, subject to legal and contractual obligations.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span><strong className="text-white">Opt-Out:</strong> Unsubscribe from marketing communications by following the unsubscribe link in emails or contacting us directly.</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span><strong className="text-white">Data Portability:</strong> Request a copy of your information in a structured, commonly used format.</span>
                  </li>
                </ul>
                <p className="mt-4">
                  To exercise these rights, please contact us using the information provided below. We will respond to your request within a reasonable timeframe and in accordance with applicable law.
                </p>
              </div>
            </div>

            {/* Section 6: Cookies and Tracking */}
            <div className="pt-28">
              <h2 className="text-3xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                6. Cookies and Tracking Technologies
              </h2>
              <div className="space-y-4 text-lg">
                <p>
                  Our website uses cookies and similar tracking technologies to enhance user experience, analyze website traffic, and improve our services. Cookies are small text files stored on your device that help us:
                </p>
                <ul className="space-y-3 ml-6">
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Remember your preferences and settings</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Understand how visitors use our website</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Improve website functionality and performance</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-gold mr-3">•</span>
                    <span>Provide relevant content and advertisements</span>
                  </li>
                </ul>
                <p className="mt-4">
                  You can control cookies through your browser settings. However, disabling cookies may affect your ability to use certain features of our website.
                </p>
              </div>
            </div>

            {/* Section 7: Children's Privacy */}
            <div className="pt-28">
              <h2 className="text-3xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                7. Children's Privacy
              </h2>
              <div className="space-y-4 text-lg">
                <p>
                  Our website and services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If we become aware that we have inadvertently collected information from a child under 18, we will take steps to delete such information promptly.
                </p>
              </div>
            </div>

            {/* Section 8: Changes to Privacy Policy */}
            <div className="pt-28">
              <h2 className="text-3xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                8. Changes to This Privacy Policy
              </h2>
              <div className="space-y-4 text-lg">
                <p>
                  We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. We will post the updated policy on this page with a revised "Last Updated" date. We encourage you to review this policy periodically to stay informed about how we protect your information.
                </p>
                <p>
                  For material changes that significantly affect your rights, we will provide prominent notice on our website or contact you directly using the information you have provided.
                </p>
              </div>
            </div>

            {/* Contact Information */}
            <div className="bg-black border border-gold/30 rounded-lg p-8 mt-12">
              <h2 className="text-3xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                9. Contact Us
              </h2>
              <div className="space-y-4 text-lg">
                <p>
                  If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
                </p>
                <div className="space-y-3 mt-6">
                  <div className="flex items-start space-x-3">
                    <div className="text-gold font-bold min-w-[120px]">Company:</div>
                    <div className="pt-28">Williams Diversified LLC</div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="text-gold font-bold min-w-[120px]">Email:</div>
                    <div className="pt-28">
                      <a href="mailto:inquire@williamsdiversified.com" className="hover:text-gold transition-colors">
                        inquire@williamsdiversified.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="text-gold font-bold min-w-[120px]">Phone:</div>
                    <div className="pt-28">
                      <a href="tel:9184933418" className="hover:text-gold transition-colors">
                        (918) 493-3418
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="text-gold font-bold min-w-[120px]">Location:</div>
                    <div className="pt-28">Tulsa, Oklahoma (Nationwide & Worldwide)</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div className="bg-black border border-gold/30 rounded-lg p-8">
              <h3 className="text-2xl font-bold text-gold mb-4">Government Contractor Notice</h3>
              <p className="text-gray-300 leading-relaxed">
                As Williams Diversified LLC pursues government contracting opportunities, we are prepared to meet additional privacy and security requirements under applicable federal, state, and local laws. We maintain readiness for compliance with all relevant regulations, including those related to government data protection standards. If you are involved in a government project with us, additional privacy terms may apply and will be communicated to you separately.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-black py-24 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h3 className="text-4xl md:text-5xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Questions About <span className="text-gold">Your Privacy?</span>
          </h3>
          <p className="text-gray-300 text-xl mb-10">
            We're here to address any concerns about your personal information
          </p>
          <Link 
            to="/contact"
            className="inline-block bg-gold hover:bg-gold-dark text-black font-bold py-5 px-12 rounded text-lg transition-all transform hover:scale-105 uppercase tracking-wider"
          >
            Contact Us
          </Link>
        </div>
      </section>
    </div>
  );
}

export default PrivacyPolicy;
